import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { HorizontalScrollContainer } from '@/components/ui/horizontal-scroll-container';

import { 
  User, 
  MapPin, 
  Calendar, 
  Clock, 
  ChevronRight,
  Eye,
  UserCheck,
  Calendar as CalendarIcon,
  CalendarCheck,
  ThumbsUp,
  ThumbsDown,
  Activity,
  Filter,
  X,
  Search,
  Building,
  ExternalLink,
  Award,
  RefreshCw
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format, formatDistanceToNow } from 'date-fns';
import { Link, useSearch, useLocation } from 'wouter';
import { JobDetailModal } from '@/components/JobDetailModal';
import { STATUS, normalizeStatus, CANDIDATE_LANES } from '@/lib/status';
import { QueryKeys, type CandidateApplicationsResponse, type CandidateApplicationDto, type TypedComponentProps } from '@/types/api';
import { registerComponentMigration } from '@/types/legacy';
import type { Application } from '@shared/schema';

// Use the unified status system from status.ts
const CANDIDATE_STATUS_LABELS = CANDIDATE_LANES;

// Status colors using unified status system - violet-based palette with semantic red/green
const STATUS_COLORS: Record<string, string> = {
  [STATUS.APPLIED]: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  [STATUS.VIEWED]: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  [STATUS.INTERVIEW_REQUESTED]: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  [STATUS.INTERVIEW_SCHEDULED]: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  [STATUS.OFFER]: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  [STATUS.REJECTED]: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  [STATUS.HIRED]: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
};

// Status icons using unified status system
const STATUS_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  [STATUS.APPLIED]: Activity,
  [STATUS.VIEWED]: Eye,
  [STATUS.INTERVIEW_REQUESTED]: UserCheck,
  [STATUS.INTERVIEW_SCHEDULED]: CalendarIcon,
  [STATUS.OFFER]: Award,
  [STATUS.REJECTED]: ThumbsDown,
  [STATUS.HIRED]: ThumbsUp
};

// Phase 5b: Legacy type alias for backward compatibility
type ApplicationWithJob = CandidateApplicationDto;

interface CandidateSwimLaneProps {
  status: string;
  applications: ApplicationWithJob[];
  onViewApplication: (application: ApplicationWithJob) => void;
}

function CandidateSwimLane({ status, applications, onViewApplication }: CandidateSwimLaneProps) {
  const Icon = STATUS_ICONS[status] || Activity;
  
  return (
    <div className="min-w-[320px] flex-shrink-0">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-gray-600" />
          <h3 className="font-semibold text-gray-900">{CANDIDATE_STATUS_LABELS[status as keyof typeof CANDIDATE_STATUS_LABELS]}</h3>
        </div>
        <Badge className="ml-2 bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300">
          {applications.length}
        </Badge>
      </div>
      
      <ScrollArea className="h-[calc(100vh-250px)]">
        <div className="space-y-3 pr-4">
          {applications.map((app) => (
            <Card 
              key={app.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => onViewApplication(app)}
              data-application-id={app.id}
            >
              <CardContent className="p-4">
                <div className="mb-3">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 line-clamp-1 mb-1">
                        {app.jobTitle || app.job?.title || 'Job Title'}
                      </h4>
                      <p className="text-sm text-gray-600 line-clamp-1">
                        {app.companyName || app.job?.companyProfile?.companyName || app.company?.name || 'Company'}
                      </p>
                    </div>
                    <Badge className={`ml-3 ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-800'}`} variant="secondary">
                      {CANDIDATE_STATUS_LABELS[status as keyof typeof CANDIDATE_STATUS_LABELS] || 'Unknown'}
                    </Badge>
                  </div>
                </div>
                
                <div className="space-y-1 text-sm text-gray-600 mb-3">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    <span className="line-clamp-1">
                      {app.jobLocation || app.job?.location || 'Location not specified'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Applied {formatDistanceToNow(new Date(app.appliedAt), { addSuffix: true })}</span>
                  </div>
                  {app.statusUpdatedAt && String(app.statusUpdatedAt) !== String(app.appliedAt) && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      <span>Updated {formatDistanceToNow(new Date(app.statusUpdatedAt), { addSuffix: true })}</span>
                    </div>
                  )}
                  {app.interviewScheduled?.start && (
                    <div className="flex items-center gap-1 text-indigo-600 font-medium">
                      <CalendarCheck className="h-3 w-3" />
                      <span>Interview: {format(new Date(app.interviewScheduled.start), 'MMM d, yyyy h:mm a')}</span>
                    </div>
                  )}
                </div>
                
                {/* Candidate actions */}
                <div className="flex gap-2 mt-3 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Use the job ID directly from the current app
                      if (app.jobId) {
                        window.dispatchEvent(new CustomEvent('openJobModal', { detail: { jobId: app.jobId } }));
                      }
                    }}
                    className="flex-1 text-xs"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    View Job
                  </Button>
                  
                  {status === STATUS.INTERVIEW_REQUESTED && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={(e) => {
                        e.stopPropagation();
                        sessionStorage.setItem('previousJobPage', '/my-applications');
                        window.location.href = `/interview-scheduling/${app.jobId}`;
                      }}
                      className="flex-1 text-xs bg-purple-600 hover:bg-purple-700"
                    >
                      <Calendar className="w-3 h-3 mr-1" />
                      {app.pendingReschedule ? 'Reschedule' : 'Schedule'}
                    </Button>
                  )}
                  
                  {status === STATUS.INTERVIEW_SCHEDULED && app.pendingReschedule && (
                    <Button
                      size="sm"
                      variant="default"
                      onClick={(e) => {
                        e.stopPropagation();
                        sessionStorage.setItem('previousJobPage', '/my-applications');
                        window.location.href = `/interview-scheduling/${app.jobId}`;
                      }}
                      className="flex-1 text-xs bg-purple-600 hover:bg-purple-700"
                    >
                      <Calendar className="w-3 h-3 mr-1" />
                      Reschedule
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          
          {applications.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>No applications in this stage</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

// Filter interface
interface ApplicationFilters {
  companyName?: string;
  jobTitle?: string;
  dateFrom?: string;
  dateTo?: string;
}

// Phase 5b: Component migration marker  
interface CandidateApplicationSwimLanesProps extends TypedComponentProps {}

export function CandidateApplicationSwimLanes({
  __typed = true,
  __phase = '5b'
}: CandidateApplicationSwimLanesProps = {}) {
  const queryClient = useQueryClient();
  
  // Phase 5b: Register component migration for rollback capability
  useEffect(() => {
    registerComponentMigration('CandidateApplicationSwimLanes', '5b', () => {
      console.warn('[Phase5b] CandidateApplicationSwimLanes rolled back to legacy types');
      // Rollback implementation would revert to 'any' types
    });
  }, []);
  
  // Filter states
  const [filters, setFilters] = useState<ApplicationFilters>({});
  
  // Modal states
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isJobDetailModalOpen, setIsJobDetailModalOpen] = useState(false);
  
  // Deep linking: URL param hooks (effect defined after applications query)
  const searchString = useSearch();
  const [, setLocation] = useLocation();
  
  // Phase 5b: Set up properly typed event listener for job modal - eliminates 'any' types
  useEffect(() => {
    const handleOpenJobModal = (event: CustomEvent<{ jobId: string }>) => {
      setSelectedJobId(event.detail.jobId);
      setIsJobDetailModalOpen(true);
    };
    
    // Phase 5b: Properly typed event listeners without 'any'
    const typedHandler = handleOpenJobModal as EventListener;
    window.addEventListener('openJobModal', typedHandler);
    return () => {
      window.removeEventListener('openJobModal', typedHandler);
    };
  }, []);
  
  // Build query string with filters
  const buildQueryString = useCallback((currentFilters: ApplicationFilters) => {
    const params = new URLSearchParams();
    Object.entries(currentFilters).forEach(([key, value]) => {
      if (value && value.trim() !== '') {
        params.append(key, value);
      }
    });
    return params.toString();
  }, []);

  // Memoize query string to prevent unnecessary recalculations
  const queryString = useMemo(() => buildQueryString(filters), [filters, buildQueryString]);

  // Phase 5b: Properly typed query with CandidateApplicationsResponse
  const { data: applications = [], isLoading } = useQuery<CandidateApplicationDto[]>({
    queryKey: QueryKeys.candidateApplications(queryString),
    queryFn: async () => {
      const endpoint = queryString ? `/api/applications/candidate?${queryString}` : '/api/applications/candidate';
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error('Failed to fetch candidate applications');
      const data = await response.json();
      // Phase 5b: Extract data from SuccessResponse wrapper
      return data.success ? data.data : data;
    },
    enabled: true,
    refetchInterval: 60000, // Refresh every 60 seconds (SSE handles real-time updates)
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true, // Refetch when window regains focus to catch updates
    refetchOnMount: true, // Always refetch on mount to ensure fresh data
    placeholderData: (previousData) => previousData
  });

  // Deep linking: parse URL params and auto-open modal (must be after applications query)
  useEffect(() => {
    if (!searchString || applications.length === 0) return;
    
    const params = new URLSearchParams(searchString);
    const applicationId = params.get('applicationId');
    const shouldOpen = params.get('open') === 'true';
    
    if (applicationId && shouldOpen) {
      const application = applications.find(app => app.id === applicationId);
      if (application) {
        sessionStorage.setItem('previousJobPage', '/my-applications');
        sessionStorage.setItem('jobModalOrigin', 'my-applications');
        setSelectedJobId(application.jobId);
        setIsJobDetailModalOpen(true);
        
        setLocation('/my-applications', { replace: true });
      }
    }
  }, [searchString, applications, setLocation]);

  // Update filters handler
  const updateFilter = useCallback((key: keyof ApplicationFilters, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({});
  }, []);
  
  // Group applications by status - use normalized status
  const applicationsByStatus = applications.reduce((acc, app) => {
    // Always use statusV2 with normalization
    const status = normalizeStatus(app.statusV2 || 'applied');
    
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(app);
    return acc;
  }, {} as Record<string, ApplicationWithJob[]>);
  

  
  const handleViewApplication = (application: ApplicationWithJob) => {
    // Set the origin context for navigation
    sessionStorage.setItem('previousJobPage', '/my-applications');
    sessionStorage.setItem('jobModalOrigin', 'my-applications');
    // Open job detail modal instead of navigating
    setSelectedJobId(application.jobId);
    setIsJobDetailModalOpen(true);
  };
  
  const handleCloseJobModal = () => {
    setIsJobDetailModalOpen(false);
    setSelectedJobId(null);
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">Loading applications...</p>
      </div>
    );
  }
  
  // Get unique companies and job titles for filters
  const uniqueCompanies = Array.from(new Set(applications.map(app => 
    app.companyName || app.job?.companyProfile?.companyName || app.company?.name
  ).filter((name): name is string => Boolean(name))));
  
  const uniqueJobTitles = Array.from(new Set(applications.map(app => 
    app.jobTitle || app.job?.title
  ).filter((title): title is string => Boolean(title))));
  
  return (
    <div className="h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">My Applications</h2>
        <p className="text-gray-600 mt-1">
          Track your application progress through the hiring process
        </p>
      </div>

      {/* Filter Bar */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filter Applications
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            {/* Company Filter */}
            <div className="min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Company
              </label>
              <Select value={filters.companyName || ''} onValueChange={(value) => updateFilter('companyName', value === 'all' ? undefined : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Companies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Companies</SelectItem>
                  {[...uniqueCompanies].sort((a, b) => a.localeCompare(b)).map((company) => (
                    <SelectItem key={company} value={company || ''}>
                      {company}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Job Title Filter */}
            <div className="min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Title
              </label>
              <Select value={filters.jobTitle || ''} onValueChange={(value) => updateFilter('jobTitle', value === 'all' ? undefined : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Job Titles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Job Titles</SelectItem>
                  {[...uniqueJobTitles].sort((a, b) => a.localeCompare(b)).map((title) => (
                    <SelectItem key={title} value={title || ''}>
                      {title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date From Filter */}
            <div className="min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applied From
              </label>
              <Input
                type="date"
                value={filters.dateFrom || ''}
                onChange={(e) => updateFilter('dateFrom', e.target.value)}
                placeholder="Start date"
              />
            </div>

            {/* Date To Filter */}
            <div className="min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Applied To
              </label>
              <Input
                type="date"
                value={filters.dateTo || ''}
                onChange={(e) => updateFilter('dateTo', e.target.value)}
                placeholder="End date"
              />
            </div>

            {/* Clear Filters Button */}
            {Object.keys(filters).some(key => filters[key as keyof ApplicationFilters]) && (
              <Button 
                variant="outline" 
                onClick={clearFilters}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Swim Lanes */}
      <div className="overflow-hidden">
        <HorizontalScrollContainer gap="gap-6">
          {Object.values(STATUS).map((status) => (
            <CandidateSwimLane
              key={status}
              status={status}
              applications={applicationsByStatus[status] || []}
              onViewApplication={handleViewApplication}
            />
          ))}
        </HorizontalScrollContainer>
      </div>
      
      {/* Job Detail Modal */}
      {selectedJobId && (
        <JobDetailModal
          jobId={selectedJobId}
          isOpen={isJobDetailModalOpen}
          onClose={handleCloseJobModal}
        />
      )}
    </div>
  );
}